import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const PHONE_REGEX = /^[6-9]\d{9}$/;

const MODAL_COPY = {
  patientOnly: {
    title: 'Add New Patient',
    description: 'Create a patient profile with the existing clinic-specific Patient ID flow.',
    submitLabel: 'Create Patient Record',
  },
  treatmentPlan: {
    title: 'Create Treatment Plan',
    description: 'Create or locate the patient by phone, then continue directly into the treatment planning flow.',
    submitLabel: 'Continue to Treatment Plan',
  },
};

function getInitialForm() {
  return {
    name: '',
    phone: '',
    age: '',
    gender: '',
    profession: '',
    address: '',
  };
}

export default function AddPatientRecordModal({
  isOpen,
  onClose,
  mode = 'patientOnly',
  onPatientCreated,
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState(getInitialForm());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(getInitialForm());
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const modalCopy = MODAL_COPY[mode] || MODAL_COPY.patientOnly;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    const normalizedPhone = String(form.phone || '').replace(/\D/g, '').slice(-10);

    if (!form.name.trim() || !normalizedPhone) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!PHONE_REGEX.test(normalizedPhone)) {
      toast.error('Invalid WhatsApp number. Must be 10 digits starting with 6/7/8/9.');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post('/patients', {
        name: form.name.trim(),
        phone: normalizedPhone,
        age: form.age ? Number(form.age) : null,
        gender: form.gender || null,
        profession: form.profession.trim() || null,
        address: form.address.trim() || null,
      });

      if (mode === 'treatmentPlan') {
        toast.success('Patient ready. Opening treatment plan.');
      } else {
        toast.success('Patient record is ready.');
      }

      if (onPatientCreated) {
        onPatientCreated(data, mode);
      }

      onClose();

      if (mode === 'treatmentPlan' && data?.id) {
        navigate(`/prescriptions/new?patientId=${data.id}`);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to create patient record');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-2xl rounded-[2rem] shadow-2xl overflow-hidden">
        <div className="p-6 border-b flex justify-between items-center bg-slate-50">
          <div>
            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">{modalCopy.title}</h2>
            <p className="text-sm text-slate-500 mt-2 max-w-xl">{modalCopy.description}</p>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">
                Patient Name *
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Full Name"
                className="w-full p-4 bg-slate-50 border rounded-2xl outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">
                Phone Number (WhatsApp) *
              </label>
              <input
                type="text"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="10-digit number"
                className="w-full p-4 bg-slate-50 border rounded-2xl outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">
                Age
              </label>
              <input
                type="number"
                name="age"
                min="0"
                value={form.age}
                onChange={handleChange}
                placeholder="Age in years"
                className="w-full p-4 bg-slate-50 border rounded-2xl outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">
                Gender
              </label>
              <select
                name="gender"
                value={form.gender}
                onChange={handleChange}
                className="w-full p-4 bg-slate-50 border rounded-2xl outline-none focus:ring-2 focus:ring-blue-600"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">
                Profession
              </label>
              <input
                type="text"
                name="profession"
                value={form.profession}
                onChange={handleChange}
                placeholder="Profession"
                className="w-full p-4 bg-slate-50 border rounded-2xl outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">
                Address
              </label>
              <textarea
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="Full residential address"
                className="w-full p-4 bg-slate-50 border rounded-2xl outline-none focus:ring-2 focus:ring-blue-600 min-h-[110px] resize-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-6">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-3 rounded-full font-bold text-slate-600 hover:bg-slate-100 transition-all"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-blue-600 text-white px-6 py-3 rounded-full font-black hover:bg-blue-700 transition-all uppercase tracking-widest disabled:opacity-60"
            >
              {submitting ? 'Saving...' : modalCopy.submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
